import * as React from "react";
import { IPickList, PickList, IPickListSelection } from "VSSUI/PickList";
import { Callout } from "OfficeFabric/Callout";
import { IFilterItemState, IFilterState, IFilter } from "VSSUI/Utilities/Filter";
import { IObservableValue } from "VSS/Core/Observable";
import { IAgileFilterContext } from "Agile/Scripts/Common/Components/AgileFilterBar/AgileFilterBar";
import {
    IWorkItemFilterItemProvider, IWorkItemFilterItem, IWorkItemFilterField,
    IWorkItemFilterPickListItem,
} from "WorkItemTracking/Scripts/Controls/Filters/WorkItemFilter";
import { makePromise } from "Presentation/Scripts/TFS/TFS.Core.Utils";
import { CoreFieldRefNames } from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { first } from "VSS/Utils/Array";
import { equals } from "VSS/Utils/String";
import { SelectionMode } from "OfficeFabric/Selection";
import { PeopleSwitcherValueProvider } from "Agile/Scripts/SprintsHub/Taskboard/PeopleSwitcherValueProvider";
import { FilterValue } from "WorkItemTracking/Scripts/Filtering/FilterManager";
import { mapToFilterState } from "WorkItemTracking/Scripts/Controls/Filters/WorkItemFilter";
import "VSS/LoaderPlugins/Css!Agile/Scripts/SprintsHub/Taskboard/Components/PeoplePickerCallout";

export interface IPeopleSwitcherCalloutProps {
    onSelectionChanged: (filterState: IFilterState) => void;
    onDismiss: () => void;
    filter: IFilter;
    calloutTarget: HTMLElement;
    filterContext: IObservableValue<IAgileFilterContext>;
}

export class PeopleSwitcherCallout extends React.Component<IPeopleSwitcherCalloutProps, {}> {
    private _pickList: IPickList;
    private _cachedUniqueValues: Promise<IWorkItemFilterItem[]>;

    public render() {
        const { calloutTarget } = this.props;
        const calloutContents = (
            <PickList
                className={"people-switcher-pick-list"}
                ref={this._setPickList}
                items={this._getItemsToRender()}
                getListItem={(item: IWorkItemFilterItem) => this._getPickListItem(item)}
                selectionMode={SelectionMode.single}
                onSelectionChanged={this._onSelectionChanged}
            />
        );
        return (
            <Callout onDismiss={this._onDismiss} onPositioned={this._focusPickList} target={calloutTarget} isBeakVisible={false}>
                {calloutContents}
            </Callout>
        );

    };

    private _setPickList = (pickList: IPickList) => {
        this._pickList = pickList;
    };

    private _focusPickList = () => {
        if (this._pickList) {
            this._pickList.focus();
            this._pickList.scrollToSelected();
        }
    };

    private _onDismiss = () => {
        this.props.onDismiss();
    };

    private _onSelectionChanged = (selection: IPickListSelection): void => {
        const filterItemState: IFilterItemState = { value: selection.selectedItems[0] };
        const filterState: IFilterState = {};
        filterState[CoreFieldRefNames.AssignedTo] = filterItemState;
        this.props.onSelectionChanged(filterState);
    };

    protected _getItemsToRender(): Promise<IWorkItemFilterItem[]> {
        if (!this._cachedUniqueValues) {
            const fieldValueProvider = this._getFieldValueProvider();
            const values = makePromise(fieldValueProvider.getItems(this._getCurrentFilterValues()));
            this._cachedUniqueValues = Promise.resolve(values);
        }
        return this._cachedUniqueValues;
    };

    /**
     * We need to show the previously stored filter values, even if that value is currently not applicable for the taskboard.
     */
    private _getCurrentFilterValues(): FilterValue[] {
        const filterState = mapToFilterState(this.props.filter.getState());
        if (filterState[CoreFieldRefNames.AssignedTo]) {
            return filterState[CoreFieldRefNames.AssignedTo].values;
        }
        return undefined;
    };

    /**
     * Called when rendering an item in a picklist.  Depending on the field different icons (etc) are rendered.
     * @param value Value to generate a picklist entry for.
     */
    protected _getPickListItem(value: IWorkItemFilterItem): IWorkItemFilterPickListItem {
        const valueProvider = this._getFieldValueProvider();
        const result = valueProvider.getListItem(value);
        if (result.promise) {
            Q(result.promise)
                .done(() => this.forceUpdate());
        }
        return result.item;
    };

    private _getFieldValueProvider(): IWorkItemFilterItemProvider {
        const field = this._getField();
        if (field && field.valueProvider) {
            return field.valueProvider;
        }
        const dataSource = this.props.filterContext.value.filterManager.dataSource;
        return new PeopleSwitcherValueProvider(dataSource);
    };

    private _getField(): IWorkItemFilterField {
        const fields = this.props.filterContext.value.filterManager.filterFields;
        return first(fields, f => equals(f.fieldName, CoreFieldRefNames.AssignedTo, true));
    };
}
